import Vue from 'vue'
import { isSamePath as _isSamePath, joinURL, normalizeURL, withQuery, withoutTrailingSlash } from 'ufo'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function createGetCounter (counterObject, defaultKey = '') {
  return function getCounter (id = defaultKey) {
    if (counterObject[id] === undefined) {
      counterObject[id] = 0
    }
    return counterObject[id]++
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function purifyData(data) {
  if (process.env.NODE_ENV === 'production') {
    return data
  }

  return Object.entries(data).filter(
    ([key, value]) => {
      const valid = !(value instanceof Function) && !(value instanceof Promise)
      if (!valid) {
        console.warn(`${key} is not able to be stringified. This will break in a production environment.`)
      }
      return valid
    }
    ).reduce((obj, [key, value]) => {
      obj[key] = value
      return obj
    }, {})
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
      continue; // Don't get the children since it will reload the template
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        try {
          Component = await Component()
        } catch (error) {
          // Handle webpack chunk loading errors
          // This may be due to a new deployment or a network problem
          if (
            error &&
            error.name === 'ChunkLoadError' &&
            typeof window !== 'undefined' &&
            window.sessionStorage
          ) {
            const timeNow = Date.now()
            const previousReloadTime = parseInt(window.sessionStorage.getItem('nuxt-reload'))

            // check for previous reload time not to reload infinitely
            if (!previousReloadTime || previousReloadTime + 60000 < timeNow) {
              window.sessionStorage.setItem('nuxt-reload', timeNow)
              window.location.reload(true /* skip cache */)
            }
          }

          throw error
        }
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,
      store: app.store,
      payload: context.payload,
      error: context.error,
      base: app.router.options.base,
      env: {"PREFIX":"qy8","MOBILE_INDEX_TYPE":1,"SHORT_NANE":"錢盈","WEB_TITLE":"錢盈娛樂城","WEB_NAME":"錢盈娛樂城","PageHead":{"layouts":{"noMobile":true,"link":[{"rel":"canonical","href":""}]},"index":{"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile"},{"rel":"alternate","media":"handheld","href":"/mobile"}]}},"about":{"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/help/about"},{"rel":"alternate","media":"handheld","href":"/mobile/help/about"}]}},"question":{"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/help/question"},{"rel":"alternate","media":"handheld","href":"/mobile/help/question"}]}},"disclaimer":{"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/help/disclaimer"},{"rel":"alternate","media":"handheld","href":"/mobile/help/disclaimer"}]}},"rule":{"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/help/rule"},{"rel":"alternate","media":"handheld","href":"/mobile/help/rule"}]}},"check":{"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/help/check"},{"rel":"alternate","media":"handheld","href":"/mobile/help/check"}]}},"maintain":{"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/help/maintain"},{"rel":"alternate","media":"handheld","href":"/mobile/help/maintain"}]}},"activity":{"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/activity/activity"},{"rel":"alternate","media":"handheld","href":"/mobile/activity/activity"}]}},"sport":{"meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"description","content":"description"},{"hid":"og:description","name":"og:description","content":"description"}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/game/sport"},{"rel":"alternate","media":"handheld","href":"/mobile/game/sport"}]}},"live":{"meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"description","content":"description"},{"hid":"og:description","name":"og:description","content":"description"}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/game/live"},{"rel":"alternate","media":"handheld","href":"/mobile/game/live"}]}},"egame":{"meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"description","content":"description"},{"hid":"og:description","name":"og:description","content":"description"}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/game/egame"},{"rel":"alternate","media":"handheld","href":"/mobile/game/egame"}]}},"lottery":{"meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"description","content":"description"},{"hid":"og:description","name":"og:description","content":"description"}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/game/lottery"},{"rel":"alternate","media":"handheld","href":"/mobile/game/lottery"}]}},"chess":{"meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"description","content":"description"},{"hid":"og:description","name":"og:description","content":"description"}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/game/chess"},{"rel":"alternate","media":"handheld","href":"/mobile/game/chess"}]}},"fish":{"meta":[{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"title"},{"hid":"og:title","name":"og:title","content":"title"},{"hid":"description","name":"description","content":"description"},{"hid":"og:description","name":"og:description","content":"description"}],"desktop":{"link":[{"rel":"alternate","media":"only screen and (max-width: 640px)","href":"/mobile/game/fish"},{"rel":"alternate","media":"handheld","href":"/mobile/game/fish"}]}}},"LoadingColor":"#0f5f70","DEF_webStyle":"qy8","DEF_webFrame":"frame03","DEF_dialogTipEntity":"1","DEF_dialogTipDigit":"1","DEF_language":"default","DEF_daynight":"common/dayNight","DEF_audioplay":"default","pc_marquee":"idxMarquee","pc_marquee04":"default","pc_header":"header/Header07","pc_game":"idxGame05","pc_info":"default","pc_idxDownload":"idxDownload","pc_jackpot":"default","pc_footer":"idxFooter01","pc_falling":"default","pc_header_li":"0","pc_member_show_appendLink":"0","pc_member_link_appendLink":null,"M_DEF_webFrame":"m_frame03","M_header_ham":"0","M_header_user":"1","M_header_btn":"1","M_header_sub":"1","M_dialogAPP":"1","M_header":"default","M_main01":"idxSlider2","M_main02":"idxMarquee","M_main03":"idxHotpro7","M_main04":"idxGame4","M_main05":"nulldiv","M_main06":"default","M_Footer":"default","M_Fmenu":"default","M_game_page":"0","M_GameBtn":"1","M_ExLink":"0","M_HideMenu":"header/m_ham07","M_dialogCSR":"dialogCSR_circle","M_Login":"login","M_Signup":"signup","APP_NAME":"qy8","APP_ENV":"production","APP_KEY":"base64:F3jemSiYA/Iy8cVY957qNj+pPWdOv5W6MxfgrbJwGQQ=","APP_DEBUG":"false","APP_URL":"http://","APP_LOCALE":"zh-TW","APP_TimeZone":"Asia/Taipei","APP_API_DOMAIN":"qy8.local.api","APP_BACKEND_DOMAIN":"7w5fja.qywin88.com","APP_FRONTEND_DOMAIN":"qywin88.com","APP_CALLBACK_DOMAIN":"api.qywin88.com","AGENT_API_DOMAIN":"agent.qywin88.com","APP_IMAGES_DOMAIN":"//img.qywin88.com","LOG_CHANNEL":"daily","DB_CONNECTION":"int","DB_HOST":"172.31.106.121","DB_PORT":"3306","DB_DATABASE":"QY8_Integration","DB_DATABASE_AG":"QY8_Agency","DB_DATABASE_MA":"QY8_Management","DB_DATABASE_MS":"QY8_MoneySystem","DB_USERNAME":"qy8_admin","DB_PASSWORD":"gH@_RbZx((4.7T.G","BROADCAST_DRIVER":"log","CACHE_DRIVER":"file","QUEUE_CONNECTION":"sync","SESSION_DRIVER":"file","SESSION_LIFETIME":"120","REDIS_HOST":"172.31.106.184","REDIS_PASSWORD":"null","REDIS_PORT":"6379","MAIL_DRIVER":"smtp","MAIL_HOST":"smtp.mailtrap.io","MAIL_PORT":"2525","MAIL_USERNAME":"null","MAIL_PASSWORD":"null","MAIL_ENCRYPTION":"null","MAIL_FROM_ADDRESS":"null","MAIL_FROM_NAME":"${APP_NAME}","AWS_ACCESS_KEY_ID":"AKIAZ762NQMOMQUE64MA","AWS_SECRET_ACCESS_KEY":"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","AWS_DEFAULT_REGION":"ap-northeast-1","AWS_BUCKET":"qy8-beyondgaming","AWS_BUCKET_PUBLIC":"bg-public-image","PUSHER_APP_ID":"","PUSHER_APP_KEY":"","PUSHER_APP_SECRET":"","PUSHER_APP_CLUSTER":"mt1","MIX_PUSHER_APP_KEY":"${PUSHER_APP_KEY}","MIX_PUSHER_APP_CLUSTER":"${PUSHER_APP_CLUSTER}","JWT_SECRET":"6euQ7Do1U7N85qaHiZ8SGqbbiuSrGEiNYqT9DTS9CbFG0vHx3KpHwUdw49kYyt7L","CODE_WAIT_TIME":"300","IMG_DISK":"s3","IMG_DISK_PUBLIC":"s3Public","MASTER_ACCOUNT":"account","UPAGENT":"qy8agent","SEND_TELEGRAM_MSG":"1","CALLBACK_BY_API":"0","ALL_CHECK":"1","DEPOSIT_MULTIPLE":"1","TRANSFER_MIN":"1","BACKEND_SELECT_LIMIT":"36500","SHOW_DISCOUNT":"0","SHOW_DEPOSIT_DISCOUNT":"0","SHOW_SHARE":"1","SHOW_LEVEL":"0","SHOW_CONVERSION":"0","SHOW_DEPOSIT":"1","SHOW_WITHDRAW":"1","SHOW_ACCOUNT_CARD":"1","SHOW_VIRTUAL_CURRENCY":"1","SHOW_JKOPAY_SETTING":"0","SHOW_LINEPAY_SETTING":"0","SIGN_CAPTCHAS":"0","SIGN_MOBILE_CAPTCHAS":"1","TRANSFER_CHAT_ID":"-756486433","TRANSFER_NOTICE":"1","TRANSFER_AMOUNT":"1000000","LINE_LINK":"","SMSACC":"","SMSPW":"","ENABLE_ROULETTE":"1","ENABLE_CHECKIN":"1","DG_LIVE_HANDICAP_DEFAULT_MINBET":"100","DG_LIVE_HANDICAP_DEFAULT_MAXBET":"50000"}
    }
    // Only set once

    if (context.req) {
      app.context.req = context.req
    }
    if (context.res) {
      app.context.res = context.res
    }

    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = withQuery(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/replace
          window.location.replace(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext) {
  if (!promises.length || appContext._redirected || appContext._errored) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }

  base = decodeURI(base).slice(0, -1) // consideration is base is normalized with trailing slash
  let path = decodeURI(window.location.pathname)

  if (base && path.startsWith(base)) {
    path = path.slice(base.length)
  }

  const fullPath = (path || '/') + window.location.search + window.location.hash

  return normalizeURL(fullPath)
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export const urlJoin = joinURL

export const stripTrailingSlash = withoutTrailingSlash

export const isSamePath = _isSamePath

export function setScrollRestoration (newVal) {
  try {
    window.history.scrollRestoration = newVal;
  } catch(e) {}
}
